import { useCallback, useEffect, useState, useRef } from 'react';
import { useMutation } from '@apollo/client';

import { queryStrings } from '@trqk/resolvers';
import { constants } from '@trqk/utils';

const { API_ERROR_MESSAGES } = constants;
const { GET_TRQK_STATEMENT_VIEW, HANDLE_PRO_INGESTION } = queryStrings;
const initialTimeouts = [500, 2000];
const pollingInterval = 5000;

interface UseTrqkStatementViewInput {
    filename: string;
    onDashboardLoaded(): void;
    statementId: Maybe<string>;
    proStatementViewUrl: string;
}

interface UseTrqkStatementViewResult {
    called: boolean;
    error: Error | null;
    loading: boolean;
}

/**
 * Polls the getStatementView endpoint for a trqkStatementView. A varied timing strategy is used,
 * so that the requests are timed as follows:
 * - first, each of the timeout values in initialTimeouts is used as the delay, in array order
 * - after that, polling continues at the pollingInterval delay
 *
 * Note that getStatementView will return a 404 when the trqkStatement is in a pending state (i.e.
 * still being generated by the lambda function); when it completes it will return a 200 with data.
 * The pattern here is to poll for results as long as the response is a 404; if it's a 200 we have
 * the data and can stop polling; if it's a non-404 error, something else went wrong and we should
 * stop polling. See endpoint docs: https://github.com/trqk/trqk-docs/blob/staging/api-docs/getStatementView/README.md
 */
export const useTrqkStatementView = ({
    filename,
    onDashboardLoaded,
    statementId,
    proStatementViewUrl,
}: UseTrqkStatementViewInput): UseTrqkStatementViewResult => {
    const [started, setStarted] = useState(false);
    const [called, setCalled] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState(false);
    const [intervalId, setIntervalId] = useState<number | null>(null);
    const [timeouts, setTimeouts] = useState(initialTimeouts);
    const [timeoutIds, setTimeoutIds] = useState([] as number[]);
    const savedCallback = useRef<() => void>();

    const cancelFutureRequests = useCallback((): void => {
        if (intervalId !== null) {
            window.clearInterval(intervalId);
        }
        timeoutIds.forEach((timeoutId: number): void => {
            window.clearTimeout(timeoutId);
        });
    }, [intervalId, timeoutIds]);

    const [handleProIngestion] = useMutation(HANDLE_PRO_INGESTION);

    const [getTrqkStatementView] = useMutation(GET_TRQK_STATEMENT_VIEW, {
        variables: {
            filename,
            statementId,
            proStatementViewUrl,
        },
        onCompleted: returnedData => {
            const returnedErrors =
                returnedData?.getTrqkStatementView?.response?.data?.errors ??
                null;

            if (returnedErrors) {
                if (
                    returnedErrors?.[0]?.message ===
                    API_ERROR_MESSAGES.TRANSFORMATION_TOO_LARGE
                ) {
                    setError(
                        new Error(API_ERROR_MESSAGES.TRANSFORMATION_TOO_LARGE),
                    );
                } else {
                    setError(
                        new Error(
                            `Errors returned from getTrqkStatementView: ${JSON.stringify(
                                returnedErrors,
                                null,
                                4,
                            )}`,
                        ),
                    );
                }
                setLoading(false);
                cancelFutureRequests();
            } else {
                const dashboard =
                    (returnedData?.getTrqkStatementView?.response
                        ?.data as ProIngestionDashboard) ?? null;
                const returnedFilename =
                    returnedData?.getTrqkStatementView?.filename ?? null;

                if (dashboard !== null) {
                    cancelFutureRequests();
                    // add `__typename` fields to PRO dashboard and save in cache
                    handleProIngestion({
                        variables: {
                            dashboard,
                            filename: returnedFilename,
                        },
                    });
                    setLoading(false);
                    onDashboardLoaded();
                }
                // Continue polling if dashboard is null
            }
        },
        onError: (): void => {
            // This happens only if we hit a non-404 error. A 404 means the trqkStatementView is
            // not yet available (and we should continue polling until it becomes available) so in
            // our use case it's not really an error.
            setLoading(false);
            cancelFutureRequests();
        },
    });

    // Parts of this are based on the logic in useInterval(), which itself is based on a blog post
    // by Dan Abramov: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
    useEffect(() => {
        savedCallback.current = getTrqkStatementView;
    }, [getTrqkStatementView]);

    const tick = (): void => {
        if (savedCallback.current) {
            savedCallback.current();
        }
    };

    useEffect(() => {
        // Each setTimout or setInterval call triggers the getTrqkStatementView API call; if the
        // response comes back with a 200, further requests are canceled.
        if (statementId !== null && timeouts.length > 0 && !started) {
            setStarted(true);
            // First, call setTimeout() using each of the initialTimeout delays
            const timeoutId = window.setTimeout((): void => {
                const nextTimeouts = [...timeouts];

                tick();
                nextTimeouts.splice(0, 1);
                setTimeouts(nextTimeouts);
            }, timeouts[0]);

            setTimeoutIds(timeoutIds.concat([timeoutId]));
            setCalled(true);
            setLoading(true);
        } else if (statementId !== null && intervalId === null) {
            // Next, call setInterval() using the pollingInterval
            setIntervalId(window.setInterval(tick, pollingInterval));
        }

        // Returning this function from the useEffect call ensures that if the consuming component
        // is unmounted, the setInterval and setTimeout callbacks are canceled.
        return cancelFutureRequests;
    }, [
        cancelFutureRequests,
        intervalId,
        started,
        statementId,
        timeoutIds,
        timeouts,
    ]);

    return {
        called,
        error,
        loading,
    };
};
